import { useCallback, useState, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { FormModal } from './FormModal';
import { TextInput } from '../inputs';
import {
  useDispatch,
  addResource,
  deleteResource,
  fetchResourceById,
} from '../../../state';
import { useRefresh } from '../../../hooks';
import { convertNullFieldsToEmptyString } from '../../../lib';
import { WarehouseAisleAPI } from '../../../types';

interface Props {
  open: boolean;
  handleClose: () => any;
  warehouseId: string | number;
  aisleId?: number;
  setViewVersion?: React.Dispatch<React.SetStateAction<number>>;
}

type State = {
  firstRow: string;
  lastRow: string;
};

const initialState: State = {
  firstRow: '',
  lastRow: '',
};

export function WarehouseAisleForm({
  open,
  handleClose,
  warehouseId,
  aisleId,
  setViewVersion,
}: Props) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const refresh = useRefresh();
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState(initialState);

  useEffect(() => {
    (async function () {
      if (aisleId) {
        const { data } = await dispatch(
          fetchResourceById<WarehouseAisleAPI>({
            baseUrl: '/warehouses',
            id: warehouseId,
            path: `aisles/${aisleId}`,
          }),
        );
        if (data) {
          setState((cur) => ({
            ...cur,
            ...convertNullFieldsToEmptyString(data),
            firstRow: data.rows?.at(0) ?? '',
            lastRow: data.rows?.at(1) ?? '',
          }));
        }
      }
    })();
  }, [warehouseId, dispatch, aisleId]);

  const handleSubmit = useCallback(
    async (values: State) => {
      setSubmitting(true);
      const payload: WarehouseAisleAPI = {
        ...values,
        id: aisleId || null,
        warehouseID: warehouseId,
        rows: [values.firstRow, values.lastRow].filter((row) => !!row),
      };
      const { error } = await dispatch(
        addResource<WarehouseAisleAPI>({
          baseUrl: `/warehouses/${warehouseId}/aisles`,
          payload,
          message: 'Changes saved',
        }),
      );
      setSubmitting(false);
      if (!error) {
        setViewVersion ? setViewVersion((cur) => ++cur) : refresh();
        handleClose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aisleId, warehouseId],
  );

  const handleDelete = useCallback(async () => {
    setSubmitting(true);
    const response = await dispatch(
      deleteResource({
        baseUrl: '/warehouses',
        id: warehouseId,
        path: `aisles/${aisleId}`,
      }),
    );
    setSubmitting(false);
    const { error } = response;
    if (!error) {
      setViewVersion ? setViewVersion((cur) => ++cur) : refresh();
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, aisleId]);

  const formik = useFormik({
    initialValues: { ...initialState, ...state },
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: schema,
  });

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={
        <span className={classes.title}>{`${
          aisleId ? 'Edit' : 'Add'
        } warehouse aisle`}</span>
      }
      callback={formik.handleSubmit}
      btnText='SAVE'
      typeSubmit
      paperProps={{ style: { width: 520 } }}
      submitting={submitting}
      isEdit={!!aisleId}
      handleDelete={handleDelete}
    >
      <TextInput
        name='firstRow'
        formikProps={formik}
        label='First Row'
        className={classes.input}
        required
      />
      <TextInput
        name='lastRow'
        formikProps={formik}
        label='Last Row'
        className={classes.input}
      />
    </FormModal>
  );
}

const useStyles = makeStyles({ name: { WarehouseAisleForm } })((theme) => ({
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
  },
  input: {
    marginBottom: 16,
    width: '100%',
  },
}));

const schema = object().shape({
  firstRow: string().required('Required'),
});
